'use client';

import { useEffect } from 'react';
import { MdTimer } from 'react-icons/md';
import { useTranslations } from 'next-intl';
import { MessageModal } from '@/components/ui/message-modal';
import { useAnnounce } from '@/hooks/useAnnounce';
import type { SessionTimeoutWarningModalProps } from './SessionTimeout.types';

const NOOP = () => {};

/**
 * The "you're about to be signed out" dialog, with a live countdown.
 *
 * Presentation only — every value arrives display-ready. Built on `MessageModal` so the
 * portal, focus trap, Escape handling, scroll lock and responsive bottom-sheet behaviour are
 * the same ones every other dialog in the app uses.
 *
 * ## Invariants:
 * - The absolute cap offers no "Stay signed in", and its dialog is not dismissible. Why:
 *   activity cannot extend the cap, so an extend button would lie; and on a customer-facing
 *   site an accidental Escape or backdrop tap silently ending a session early is worse than a
 *   dialog that must be read. (Back Office maps close to "sign out now" here — deliberate
 *   divergence, PB-5476.)
 * - The countdown is not a live region. Why: announcing a new number every second is
 *   screen-reader spam. One announcement fires on the transition into the warning instead.
 */
export const SessionTimeoutWarningModal = ({
  reason,
  secondsRemaining,
  canExtend,
  onStaySignedIn,
  onSignOutNow,
}: SessionTimeoutWarningModalProps) => {
  const t = useTranslations('SessionTimeout');
  const announce = useAnnounce();

  useEffect(() => {
    announce(t('warningAnnouncement'));
    // Announce once per warning episode, not once per tick.
  }, [reason, announce, t]);

  const isIdle = reason === 'idle';

  return (
    <MessageModal
      open
      data-cy="session-timeout-modal"
      ariaLabel={isIdle ? t('idleTitle') : t('absoluteTitle')}
      icon={<MdTimer size={48} className="tw:text-warn" aria-hidden="true" />}
      title={isIdle ? t('idleTitle') : t('absoluteTitle')}
      showCloseButton={canExtend}
      onClose={canExtend ? onStaySignedIn : NOOP}
      message={
        <>
          <span
            data-cy="session-timeout-countdown"
            aria-live="off"
            className="tw:block tw:text-32 tw:font-bold tw:text-font tw:tabular-nums"
          >
            {secondsRemaining}
          </span>
          {isIdle
            ? t('idleBody', { seconds: secondsRemaining })
            : t('absoluteBody', { seconds: secondsRemaining })}
        </>
      }
      primaryAction={
        canExtend
          ? {
              label: t('staySignedIn'),
              onClick: onStaySignedIn,
              'data-cy': 'session-timeout-stay-signed-in',
            }
          : undefined
      }
      secondaryAction={{
        label: t('signOutNow'),
        onClick: onSignOutNow,
        'data-cy': 'session-timeout-sign-out-now',
      }}
    />
  );
};

SessionTimeoutWarningModal.displayName = 'SessionTimeoutWarningModal';
